import React from 'react';
import {ForecastMapFrame} from '../api/types';

// Same cadence as the rain radar playback (see useRainRadar).
export const FORECAST_MAP_FRAME_INTERVAL_MS = 750;

// `time` is epoch seconds over the wire (DRF DATETIME_FORMAT="%s"); accept
// ISO too so the hook doesn't silently break on a differently-serialized
// fixture or endpoint.
const frameTimeMs = (time: string): number => {
  const seconds = Number(time);
  return Number.isNaN(seconds) ? Date.parse(time) : seconds * 1000;
};

// Where a fresh dataset should open: the first frame at/after now — a
// completed run starts hours in the past, and "what's coming" beats
// re-reading the run from its first hour. A run entirely in the past
// settles on its last frame (the one closest to now).
const initialFrameIndex = (frames: ForecastMapFrame[]): number => {
  if (frames.length === 0) {
    return 0;
  }
  const now = Date.now();
  const upcoming = frames.findIndex((frame) => frameTimeMs(frame.time) >= now);
  return upcoming === -1 ? frames.length - 1 : upcoming;
};

/**
 * Headless flipbook player over a forecast-map frames array.
 *
 * Platform-agnostic on purpose: no DOM/Image usage so it can back both the
 * webapp viewer and the mobile screen. Frame preloading is the consumer's
 * responsibility (e.g. `new Image()` in the webapp layer).
 *
 * A fresh dataset (mount, or a config switch — the screens pass an empty
 * array while the next manifest loads) opens on the first frame at/after
 * now; a refresh of an already-playing dataset only clamps, preserving the
 * user's position. While playing, the index auto-advances every 750 ms and
 * loops back to the first frame after the last one.
 */
const useForecastMapPlayer = (
  frames: ForecastMapFrame[],
  initialPlaying = false,
) => {
  const frameCount = frames.length;

  // The count the index belongs to, which is what tells a fresh dataset
  // (empty → frames) from a refresh of the current one.
  const [position, setPosition] = React.useState(() => ({
    count: frameCount,
    index: initialFrameIndex(frames),
  }));

  // Adjusted while rendering rather than from an effect (FE #180). React
  // re-runs the hook with the new position and throws this pass away, so the
  // index is never briefly out of range for the frames being handed out; the
  // effect version rendered the stale one first. The guard is a condition its
  // own update makes false, so it cannot loop.
  let current = position;
  if (current.count !== frameCount) {
    current = {
      count: frameCount,
      index:
        frameCount === 0
          ? 0
          : current.count === 0
            ? // Reads the clock, which `react-hooks/purity` does not see
              // through a module-level helper -- and does not need to: this
              // runs only on the empty -> frames transition, and a discarded
              // render simply re-reads it.
              initialFrameIndex(frames)
            : // Same dataset refreshed: clamp instead of hard reset so a
              // same-length refresh keeps the current position.
              Math.min(current.index, frameCount - 1),
    };
    setPosition(current);
  }

  const index = current.index;

  const setIndexState = React.useCallback(
    (value: number | ((previous: number) => number)) =>
      setPosition((previous) => {
        const next =
          typeof value === 'function' ? value(previous.index) : value;
        // Returning the same object keeps the bail-out a plain `useState`
        // gave for free: scrubbing to the frame already on screen, or a tick
        // that lands on it, must not cost a render.
        return next === previous.index
          ? previous
          : {count: previous.count, index: next};
      }),
    [],
  );

  const [playing, setPlaying] = React.useState(initialPlaying);

  // Auto-advance while playing, looping past the last frame.
  React.useEffect(() => {
    if (playing && frameCount > 1) {
      const timerId = setInterval(() => {
        setIndexState((current) => (current + 1) % frameCount);
      }, FORECAST_MAP_FRAME_INTERVAL_MS);
      return () => clearInterval(timerId);
    }
  }, [playing, frameCount, setIndexState]);

  const setIndex = React.useCallback(
    (value: number) => {
      if (frameCount === 0) {
        return;
      }
      setIndexState(Math.min(Math.max(value, 0), frameCount - 1));
    },
    [frameCount, setIndexState],
  );

  const next = React.useCallback(() => {
    if (frameCount === 0) {
      return;
    }
    setIndexState((current) => (current + 1) % frameCount);
  }, [frameCount, setIndexState]);

  const prev = React.useCallback(() => {
    if (frameCount === 0) {
      return;
    }
    setIndexState((current) => (current - 1 + frameCount) % frameCount);
  }, [frameCount, setIndexState]);

  const play = React.useCallback(() => setPlaying(true), []);
  const pause = React.useCallback(() => setPlaying(false), []);

  const currentFrame: ForecastMapFrame | undefined = frames[index];

  return React.useMemo(
    () => ({
      index,
      currentFrame,
      playing,
      play,
      pause,
      setIndex,
      next,
      prev,
    }),
    [index, currentFrame, playing, play, pause, setIndex, next, prev],
  );
};

export default useForecastMapPlayer;
